'use client';
import React, { useState } from 'react';
import { Info, ChevronDown } from 'lucide-react';
import { DISTRIBUTORS, GENERATION_DATA, getEBRate, getEBTierLabel, getRankDotColor } from '@/data/mockData';
import RankBadge from '@/components/ui/RankBadge';
import WealthMaximizerComparison from './WealthMaximizerComparison';
import GenerationTreeView from './GenerationTreeView';
import ExecutiveDetailRows from './ExecutiveDetailRows';

export default function BreakawayExplorerClient() {
  const [selectedRootId, setSelectedRootId] = useState('dist-001');
  const [showRootDrop, setShowRootDrop] = useState(false);

  const execDistributors = Object.values(DISTRIBUTORS)?.filter(d =>
    ['Executive', 'Emerald', 'Diamond', 'Blue Diamond', 'Presidential Director']?.includes(d?.rank)
  );

  const root = DISTRIBUTORS?.[selectedRootId];
  const ebRate = getEBRate(root.gsv);
  const hasEEB = root.gsv >= 3000;
  const hasDblG1 = root.gsv >= 3000;

  const execBonus = root.gsv * ebRate;
  const eebBonus = hasEEB ? root.gsv * 0.05 : 0;
  const dblG1Bonus = hasDblG1 && root.g1BreakawayCount > 0 ? root.gsv * 0.10 : 0;
  const breakawayBonus = root.g1BreakawayCount >= 1 ? root.gsv * 0.025 : 0;

  const volumePath = execBonus + eebBonus + dblG1Bonus;
  const depthPath = execBonus + breakawayBonus;
  const wealthMaxBonus = Math.max(volumePath, depthPath);
  const wealthPath = volumePath >= depthPath ? 'Volume Maximizer' : 'Depth Maximizer';

  const totalGenBonus = GENERATION_DATA?.reduce((s, g) => s + g?.bonus, 0);
  const totalComm = execBonus + wealthMaxBonus + totalGenBonus;

  return (
    <div className="flex flex-col h-full bg-zinc-950">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-zinc-800 bg-zinc-900/40">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-zinc-100">Breakaway & Generation Commission Explorer</h1>
            <p className="text-xs text-zinc-500 mt-0.5">Visualize G1–G6 generation commissions, Wealth Maximizer paths, and breakaway structure payouts</p>
          </div>

          {/* Executive selector */}
          <div className="relative">
            <button
              onClick={() => setShowRootDrop(!showRootDrop)}
              className="flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-2 text-sm text-zinc-200 hover:border-zinc-600 transition-colors"
            >
              <div className={`w-2 h-2 rounded-full ${getRankDotColor(root.rank)}`} />
              <span className="font-medium">{root.name}</span>
              <ChevronDown size={14} className="text-zinc-500" />
            </button>
            {showRootDrop && (
              <div className="absolute right-0 top-full mt-1 z-50 bg-zinc-800 border border-zinc-700 rounded-xl shadow-2xl py-1 w-60 animate-fade-in">
                {execDistributors?.map(d => (
                  <button
                    key={`esel-${d?.id}`}
                    onClick={() => { setSelectedRootId(d?.id); setShowRootDrop(false); }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-zinc-700/50 transition-colors ${selectedRootId === d?.id ? 'bg-violet-600/10' : ''}`}
                  >
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getRankDotColor(d?.rank)}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-zinc-200 truncate">{d?.name}</p>
                      <p className="text-[10px] text-zinc-500">{d?.rank}</p>
                    </div>
                    <span className="text-[10px] font-mono text-zinc-600">{getEBTierLabel(d?.gsv)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        <div className="max-w-screen-2xl mx-auto px-6 py-5 space-y-5">
          {/* Executive Summary */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-600/30 to-violet-800/20 border border-violet-500/20 flex items-center justify-center text-lg font-bold text-violet-300 flex-shrink-0">
                {root.name?.split(' ')?.map(n => n?.[0])?.join('')}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="text-base font-bold text-zinc-100">{root.name}</h2>
                  <RankBadge rank={root.rank} size="sm" />
                  <span className="text-xs font-mono text-zinc-600">{root.id}</span>
                </div>
                <div className="grid grid-cols-6 gap-3 mt-3">
                  {[
                    { label: 'Own GSV', value: `${(root.gsv / 1000)?.toFixed(1)}K`, color: 'text-zinc-100' },
                    { label: 'PSV', value: root.psv?.toString(), color: root.psv >= 100 ? 'text-zinc-100' : 'text-red-400' },
                    { label: 'EB Tier', value: getEBTierLabel(root.gsv), color: 'text-amber-400' },
                    { label: 'G1 Execs', value: root.g1BreakawayCount?.toString(), color: 'text-violet-400' },
                    { label: 'Wealth Path', value: wealthPath?.split(' ')?.[0], color: 'text-emerald-400' },
                    { label: 'Total Est.', value: `$${totalComm?.toFixed(0)}`, color: 'text-emerald-400' },
                  ]?.map(s => (
                    <div key={`bexs-${s?.label}`} className="bg-zinc-800/50 rounded-xl p-2.5 text-center">
                      <p className="text-[9px] uppercase tracking-wider text-zinc-600 font-semibold">{s?.label}</p>
                      <p className={`text-sm font-bold font-mono mt-1 ${s?.color}`}>{s?.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Wealth Maximizer Comparison */}
          <WealthMaximizerComparison
            gsv={root.gsv}
            g1Count={root.g1BreakawayCount}
            ebRate={ebRate}
            hasEEB={hasEEB}
            hasDblG1={hasDblG1}
          />

          {/* Two-column: Generation Tree + Detail */}
          <div className="grid grid-cols-2 gap-5">
            <GenerationTreeView generationData={GENERATION_DATA} />
            <ExecutiveDetailRows generationData={GENERATION_DATA} />
          </div>

          {/* Info Panel */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Info size={14} className="text-blue-400" />
              <h3 className="text-sm font-semibold text-zinc-300">NuSkin Breakaway & Generation Commission Rules</h3>
            </div>
            <div className="grid grid-cols-3 gap-4 text-xs text-zinc-500">
              <div>
                <p className="font-semibold text-zinc-300 mb-1">Breakaway Executive Bonus</p>
                <p>Earns up to 5% on GSV of G1–G6 breakaway executives. Requires own GSV ≥ 2,000. Full 5% requires GSV ≥ 3,000. At 2,000–2,999 GSV, rate is 2.5%.</p>
              </div>
              <div>
                <p className="font-semibold text-zinc-300 mb-1">Double G1 Bonus (DBLG1)</p>
                <p>10% bonus on total GSV of G1 executives. Only available when own GSV ≥ 3,000. Part of the Volume Maximizer path alongside EEB.</p>
              </div>
              <div>
                <p className="font-semibold text-zinc-300 mb-1">Wealth Maximizer Logic</p>
                <p>Automatically pays the higher of: Volume Path (EB + EEB + DBLG1) or Depth Path (EB + Breakaway EB). You never have to choose — the system picks the better result.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}